import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const portalType = searchParams.get('portal') || null; // 'admin' | 'partner' | 'client'
  const backLink = portalType === 'admin' ? '/admin/login' : portalType === 'client' ? '/client/login' : '/partner/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address'); return; }
    setLoading(true);
    setError('');
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        frontend_base_url: window.location.origin,
      };
      if (portalType) payload.portal_type = portalType;
      await axios.post(`${API}/auth/forgot-password`, payload);
      setDone(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm w-full max-w-sm p-8">
        {done ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-7 w-7 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Check your inbox</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
            If <strong>{email}</strong> is registered, we've sent a password reset link to that address. The link expires in <strong>10 minutes</strong>.
            </p>
            <Link to={backLink} className="block mt-4">
              <Button variant="outline" className="w-full">Back to Login</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <Link to={backLink} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to Login
              </Link>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-6 w-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Forgot password?</h1>
              <p className="text-sm text-slate-500 mt-1">Enter your registered email and we'll send you a reset link.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="fp-email">Email address</Label>
                <Input
                  id="fp-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  autoFocus
                  data-testid="fp-email-input"
                />
                {error && <p className="text-xs text-red-500">{error}</p>}
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="fp-submit-btn">
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
